import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, ArrowDownward, ArrowUpward, Delete, Edit } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { diagnosticoService } from '@/services/diagnosticoService'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import {
  CampoDiagnostico,
  CampoDiagnosticoInput,
  TipoCampoDiagnostico,
} from '@/types/estruturacao'

/**
 * Cadastro dos campos do Diagnóstico de um produto (etapa 1 da Estruturação).
 *
 * **Por que isto é uma tela.** O formulário que o consultor responde na estruturação é
 * montado a partir daqui — campos são dado, não código, como as perguntas de
 * elegibilidade e o checklist documental ao lado. O backend já tinha o CRUD (ADMIN)
 * desde a Onda 4; faltava a tela, e a mensagem de vazio da estruturação mandava o
 * usuário para um lugar que não existia.
 *
 * **Desativar não é o mesmo que remover.** O consultor só vê os campos ativos, mas as
 * respostas já dadas apontam para o campo: desativar tira do formulário e preserva o
 * histórico; remover apaga a definição. As duas ações existem, com nomes distintos.
 */

const TIPOS: { valor: TipoCampoDiagnostico; rotulo: string; ajuda: string }[] = [
  { valor: 'TEXTO', rotulo: 'Texto', ajuda: 'Resposta livre em uma linha' },
  { valor: 'NUMERO', rotulo: 'Número', ajuda: 'Quantidades, prazos, percentuais' },
  { valor: 'MOEDA', rotulo: 'Moeda (R$)', ajuda: 'Valores em reais' },
  { valor: 'DATA', rotulo: 'Data', ajuda: 'Uma data no calendário' },
  { valor: 'OPCAO', rotulo: 'Escolha de lista', ajuda: 'Alternativas fixas, uma por linha' },
]

const rotuloTipo = (t: TipoCampoDiagnostico): string =>
  TIPOS.find((x) => x.valor === t)?.rotulo ?? t

/** Formulário do diálogo — `opcoes` é texto (uma por linha) e vira lista no envio. */
interface Formulario {
  rotulo: string
  tipo: TipoCampoDiagnostico
  opcoes: string
  obrigatorio: boolean
  ativo: boolean
}

const VAZIO: Formulario = {
  rotulo: '',
  tipo: 'TEXTO',
  opcoes: '',
  obrigatorio: false,
  ativo: true,
}

interface Props {
  produtoId: string
  /** Somente ADMIN edita; os demais veem o formulário que o consultor vai responder. */
  podeEditar: boolean
}

const CamposDiagnostico: React.FC<Props> = ({ produtoId, podeEditar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [campos, setCampos] = useState<CampoDiagnostico[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [editando, setEditando] = useState<CampoDiagnostico | null>(null)
  const [form, setForm] = useState<Formulario>(VAZIO)
  const [aberto, setAberto] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [reordenando, setReordenando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      // Quem edita precisa ver também os inativos (para reativar); quem só consulta vê
      // exatamente o que o consultor responde.
      setCampos(
        podeEditar
          ? await diagnosticoService.camposDoProdutoParaCadastro(produtoId)
          : await diagnosticoService.camposDoProduto(produtoId)
      )
    } catch {
      setErro('Não foi possível carregar os campos de diagnóstico.')
    } finally {
      setCarregando(false)
    }
  }, [produtoId, podeEditar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrirNovo = () => {
    setEditando(null)
    setForm(VAZIO)
    setAberto(true)
  }

  const abrirEdicao = (c: CampoDiagnostico) => {
    setEditando(c)
    setForm({
      rotulo: c.rotulo,
      tipo: c.tipo,
      opcoes: (c.opcoes ?? []).join('\n'),
      obrigatorio: !!c.obrigatorio,
      ativo: c.ativo !== false,
    })
    setAberto(true)
  }

  /** Texto do diálogo → payload do backend. A ordem é a posição na lista. */
  const paraEnvio = (ordem: number): CampoDiagnosticoInput => ({
    ordem,
    rotulo: form.rotulo.trim(),
    tipo: form.tipo,
    opcoes:
      form.tipo === 'OPCAO'
        ? form.opcoes
            .split('\n')
            .map((o) => o.trim())
            .filter(Boolean)
        : undefined,
    obrigatorio: form.obrigatorio,
    ativo: form.ativo,
  })

  const salvar = async () => {
    setSalvando(true)
    try {
      if (editando) {
        const posicao = campos.findIndex((c) => c.id === editando.id)
        await diagnosticoService.atualizarCampo(
          produtoId,
          editando.id,
          paraEnvio(posicao >= 0 ? posicao : (editando.ordem ?? 0))
        )
      } else {
        await diagnosticoService.criarCampo(produtoId, paraEnvio(campos.length))
      }
      enqueueSnackbar('Campo salvo. Vale para as próximas estruturações deste produto.', {
        variant: 'success',
      })
      setAberto(false)
      await carregar()
    } catch (e) {
      // O backend recusa OPCAO sem alternativa — a mensagem dele diz por quê.
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao salvar o campo.', {
        variant: 'error',
      })
    } finally {
      setSalvando(false)
    }
  }

  const remover = async (c: CampoDiagnostico) => {
    try {
      await diagnosticoService.removerCampo(produtoId, c.id)
      enqueueSnackbar('Campo removido.', { variant: 'success' })
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao remover o campo.', { variant: 'error' })
    }
  }

  /**
   * Sobe/desce um campo. Não há endpoint de reordenação em lote: a ordem é gravada campo
   * a campo, então mover é reenviar os DOIS que trocaram de lugar.
   */
  const mover = async (indice: number, direcao: -1 | 1) => {
    const destino = indice + direcao
    if (destino < 0 || destino >= campos.length) return
    setReordenando(true)
    try {
      const a = campos[indice]
      const b = campos[destino]
      await diagnosticoService.atualizarCampo(produtoId, a.id, {
        ordem: destino,
        rotulo: a.rotulo,
        tipo: a.tipo,
        opcoes: a.tipo === 'OPCAO' ? a.opcoes : undefined,
        obrigatorio: !!a.obrigatorio,
        ativo: a.ativo !== false,
      })
      await diagnosticoService.atualizarCampo(produtoId, b.id, {
        ordem: indice,
        rotulo: b.rotulo,
        tipo: b.tipo,
        opcoes: b.tipo === 'OPCAO' ? b.opcoes : undefined,
        obrigatorio: !!b.obrigatorio,
        ativo: b.ativo !== false,
      })
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao reordenar os campos.', { variant: 'error' })
    } finally {
      setReordenando(false)
    }
  }

  const opcoesVazias =
    form.tipo === 'OPCAO' && form.opcoes.split('\n').every((o) => !o.trim())

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando campos de diagnóstico" />
      </Box>
    )
  }

  return (
    <SecaoProduto
      titulo="Campos do diagnóstico"
      descricao="O que o consultor responde na etapa Diagnóstico da estruturação deste produto. Sem campos, a etapa abre vazia."
      acao={
        podeEditar ? (
          <Button variant="outlined" startIcon={<Add />} onClick={abrirNovo}>
            Novo campo
          </Button>
        ) : undefined
      }
    >

      {erro && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void carregar()}>
              Tentar novamente
            </Button>
          }
        >
          {erro}
        </Alert>
      )}

      {campos.length === 0 ? (
        <Alert severity="info">
          Nenhum campo cadastrado — a etapa Diagnóstico deste produto abre vazia para o
          consultor.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {campos.map((c, i) => (
            <Card key={c.id} sx={{ p: 2, opacity: c.ativo === false ? 0.55 : 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {c.rotulo}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {rotuloTipo(c.tipo)}
                    {c.tipo === 'OPCAO' && (c.opcoes ?? []).length > 0
                      ? ` · ${c.opcoes.join(' · ')}`
                      : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {c.obrigatorio && (
                    <Chip size="small" label="Obrigatório" color="primary" variant="outlined" />
                  )}
                  {c.ativo === false && <Chip size="small" label="Inativo" />}
                  {podeEditar && (
                    <>
                      <Tooltip title="Subir">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Subir ${c.rotulo}`}
                            disabled={i === 0 || reordenando}
                            onClick={() => void mover(i, -1)}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Descer">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Descer ${c.rotulo}`}
                            disabled={i === campos.length - 1 || reordenando}
                            onClick={() => void mover(i, 1)}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          aria-label={`Editar ${c.rotulo}`}
                          onClick={() => abrirEdicao(c)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          aria-label={`Remover ${c.rotulo}`}
                          onClick={() => void remover(c)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editando ? 'Editar campo' : 'Novo campo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Rótulo"
              value={form.rotulo}
              onChange={(e) => setForm({ ...form, rotulo: e.target.value })}
              placeholder="Volume mensal de contas a pagar"
              helperText="É o texto que o consultor lê no formulário da estruturação."
              fullWidth
            />

            <TextField
              label="Tipo do campo"
              select
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoCampoDiagnostico })}
              helperText={TIPOS.find((t) => t.valor === form.tipo)?.ajuda}
              fullWidth
            >
              {TIPOS.map((t) => (
                <MenuItem key={t.valor} value={t.valor}>
                  {t.rotulo}
                </MenuItem>
              ))}
            </TextField>

            {form.tipo === 'OPCAO' && (
              <TextField
                label="Alternativas (uma por linha)"
                value={form.opcoes}
                onChange={(e) => setForm({ ...form, opcoes: e.target.value })}
                error={opcoesVazias}
                helperText={
                  opcoesVazias
                    ? 'Um campo de escolha sem alternativas seria impossível de responder.'
                    : 'O consultor escolhe uma destas opções.'
                }
                multiline
                rows={4}
                fullWidth
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={form.obrigatorio}
                  onChange={(e) => setForm({ ...form, obrigatorio: e.target.checked })}
                />
              }
              label="Campo obrigatório no diagnóstico"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.ativo}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                />
              }
              label={
                form.ativo
                  ? 'Campo ativo — aparece para o consultor'
                  : 'Campo inativo — sai do formulário, respostas já dadas são preservadas'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void salvar()}
            disabled={salvando || !form.rotulo.trim() || opcoesVazias}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </SecaoProduto>
  )
}

export default CamposDiagnostico
